/* eslint-disable react/no-unstable-nested-components */
import {
  Box,
  ButtonGroup,
  Circle,
  Flex,
  HStack,
  Icon,
  Link,
  Progress,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ColumnDef, PaginationState, createColumnHelper } from '@tanstack/react-table';
import { Navigate, Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, SingleDocument, Wifi } from '@/client/components/icons/ContinuIcons';
import {
  QueryListWorkflowsArgs,
  Workflow,
  WorkflowEdge,
  WorkflowType,
} from '@/client/services/api/graphql/gql/graphql';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import AdminAssignmentsService from '../api/AdminAssignmentsService';
import AssignmentConfirmationModal from '@/client/components/admin/assignments/overlay/AssignmentConfirmationModal';
import AssignmentEditNameModal from '../overlay/AssignmentEditNameModal';
import AssignmentTabButton from '../buttons/AssignmentTabButton';
import AssignmentsTable from '../tables/AssignmentsTable';
import CreateAssignmentStartPage from '../builder/CreateAssignmentStartPage';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import WarmNeutralTableActionsMenu from '../tables/WarmNeutralTableActionsMenu';
import WorkflowSearch from '../input/WorkflowSearch';
import { format } from 'date-fns';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useAdminAssignmentsStore } from '../state/adminAssignmentsStore';
import { useCheckAccessProfile } from '../hooks/useCheckAccessProfile';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import { useWorkflowActions } from '../hooks/useWorkflowActions';
import { useWorkflowSearchStore } from '../state/workflowSearchStore';

export default function AssignmentsOverview() {
  useCheckAccessProfile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allowance, isAllowed } = useAccessHelper();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedItems, selectedFilterOption } = useWorkflowSearchStore();
  const { setSelectedInstanceCreationData, setSelectedWorkflowDuplicateData } =
    useAdminAssignmentsStore();
  const { deactivateWorkflowMutation, activateWorkflowMutation } = useWorkflowActions();
  const { setToast } = useToastStore();
  const [columnVisibility, setColumnVisibility] = useState<{ [key: string]: boolean }>({});
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [editNameModalOpen, setEditNameModalOpen] = useState(false);
  const [confirmationModalType, setConfirmationModalType] = useState<
    'unassign' | 'remind' | 'markCompleted' | 'duplicate' | 'delete' | null
  >(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');

  useEffect(() => {
    localStorage.removeItem('learn-app-assignment-content-selection');
  }, []);

  const permissions = {
    edit: isAllowed(['admin', 'assignments', 'edit'], null, ['admin', 'creator', 'manager']),
    view: isAllowed(['admin', 'assignments', 'view'], null, ['admin', 'creator', 'manager']),
    archive: isAllowed(['admin', 'assignments', 'archive'], null, ['admin', 'creator', 'manager']),
    complete: isAllowed(['admin', 'assignments', 'complete'], null, [
      'admin',
      'creator',
      'manager',
    ]),
  };

  const {
    assignments: assignmentsAllowed,
    workflows: workflowsAllowed,
    assignmentsCreate,
  } = allowance;

  const type = searchParams.get('type');

  const pageTitleMap = {
    all: t('admin.assignments.allAssignments'),
    direct: t('admin.assignments.directAssignments'),
    automated: t('admin.assignments.automatedAssignments'),
  };

  useEffect(() => {
    // * Reset type if current type isn't allowed
    if (!assignmentsAllowed) {
      // If assignments not allowed, only automated workflows can be viewed
      setSearchParams({ type: 'automated' });
      return;
    }

    if (!workflowsAllowed) {
      setSearchParams({ type: 'direct' });
      return;
    }

    // * Set column visibility based on type
    if (type === 'all') {
      setColumnVisibility({
        instances: false,
        status: false,
        actions: true,
      });
    }

    if (type === 'direct') {
      setColumnVisibility({
        type: false,
        status: false,
        actions: true,
      });
    }

    if (type === 'automated') {
      setColumnVisibility({
        type: false,
        instances: false,
        actions: true,
      });
    }
  }, [type, assignmentsAllowed]);

  useEffect(() => {
    if (!type) {
      if (assignmentsAllowed && workflowsAllowed) {
        setSearchParams({ type: 'all' });
      } else if (assignmentsAllowed) {
        setSearchParams({ type: 'direct' });
      } else if (workflowsAllowed) {
        setSearchParams({ type: 'automated' });
      }
    }

    setSelectedInstanceCreationData(null);
    setSelectedWorkflowDuplicateData(null);
  }, []);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const { data, refetch, isLoading, isFetching } = useQuery({
    queryKey: [
      'listWorkflows',
      { page: pageIndex, pageSize },
      type,
      selectedFilterOption.value,
      selectedItems,
    ],
    queryFn: () => {
      let payload: QueryListWorkflowsArgs = {
        includeTotalCount: true,
        page: pageIndex,
        pageSize,
      };

      if (type === 'direct') {
        payload = {
          ...payload,
          pointInTime: { eq: true },
        };
      }

      if (type === 'automated') {
        payload = {
          ...payload,
          pointInTime: { eq: false },
        };
      }

      if (selectedItems && selectedItems.length > 0) {
        if (selectedFilterOption.value === 'assignments') {
          payload = {
            ...payload,
            // @ts-ignore
            name: { contains: selectedItems[0].title },
          };
        }

        if (selectedFilterOption.value === 'users') {
          payload = {
            ...payload,
            userId: { in: selectedItems.map((item) => item.id) },
          };
        }

        if (selectedFilterOption.value === 'content') {
          payload = {
            ...payload,
            contentId: { in: selectedItems.map((item) => item.id) },
          };
        }
      }

      return AdminAssignmentsService.listWorkflows(payload);
    },

    keepPreviousData: true,
  });

  const getRowId = useCallback((row: WorkflowEdge) => row.node.id, []);

  const columnHelper = createColumnHelper<WorkflowEdge>();

  const columns = useMemo<ColumnDef<WorkflowEdge, any>[]>(
    () => [
      columnHelper.accessor('node.settings.name', {
        id: 'name',
        header: `${t('admin.assigments.tableHeader.name')}`,
        cell: (info) => (
          <Link
            as={RouterLink}
            color="baseBlack"
            to={`/admin/assignments/details/${info.row.original.node.id}`}
            _hover={{ textDecoration: 'underline' }}
          >
            {info.getValue()}
          </Link>
        ),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('node.resultCount', {
        id: 'resultCount',
        header: `${t('admin.assigments.tableHeader.sentTo')}`,
        cell: (info) => (
          <Box
            id="userCount"
            backgroundColor="transparent"
            borderRadius="8px"
            paddingY={2}
            paddingX={4}
            marginLeft={-4}
            border="1px solid"
            borderColor="transparent"
          >
            {`${info.getValue()} ${t('admin.assignments.users')}`}
          </Box>
        ),
      }),
      columnHelper.display({
        id: 'instances',
        header: `${t('admin.assigments.tableHeader.instances')}`,
        cell: (info) => <span>{info.row.original.node.resultGroupCount}</span>,
      }),
      columnHelper.display({
        id: 'status',
        header: `${t('admin.assigments.tableHeader.status')}`,
        cell: (info) => {
          const { enabled } = info.row.original.node;

          return (
            <HStack>
              {enabled && (
                <Circle size={4} backgroundColor="rgba(0, 175, 79, .2)">
                  <Circle size={2} backgroundColor="rgba(0, 175, 79, 1)" />
                </Circle>
              )}

              <Text color={enabled ? '#00AF4F' : 'unset'}>
                {enabled
                  ? t('admin.assigments.tableHeader.status.active')
                  : t('admin.assigments.tableHeader.status.inactive')}
              </Text>
            </HStack>
          );
        },
      }),
      columnHelper.accessor('node.settings.type', {
        id: 'type',
        header: `${t('admin.assigments.tableHeader.type')}`,
        cell: (info) => {
          const { type: workflowType } = info.row.original.node.settings;

          return (
            <HStack
              spacing={1}
              backgroundColor={
                workflowType === WorkflowType.PointInTime ? 'warmNeutral.100' : '#DDF8E5'
              }
              paddingY={1}
              paddingX={2}
              borderRadius="4px"
              justifyContent="center"
              width="fit-content"
            >
              <Circle
                size={5}
                backgroundColor={
                  workflowType === WorkflowType.PointInTime ? 'warmNeutral.200' : '#C9EFD4'
                }
              >
                <Icon
                  boxSize={3}
                  as={workflowType === WorkflowType.PointInTime ? SingleDocument : Wifi}
                  color={workflowType === WorkflowType.PointInTime ? 'warmNeutral.900' : '#2CBE75'}
                />
              </Circle>

              <Text
                color={workflowType === WorkflowType.PointInTime ? 'warmNeutral.900' : '#2CBE75'}
              >
                {workflowType === WorkflowType.PointInTime
                  ? t('admin.assignments.type.direct')
                  : t('admin.assignments.type.automated')}
              </Text>
            </HStack>
          );
        },

        footer: (info) => info.column.id,
      }),
      columnHelper.display({
        id: 'percentComplete',
        header: `${t('admin.assigments.tableHeader.completion')}`,
        cell: (info) => {
          const percentComplete = Math.round(info.row.original.node.percentComplete);

          return (
            <HStack>
              <Progress variant="assignmentsTable" width="full" value={percentComplete} />

              <Text>{`${percentComplete}%`}</Text>
            </HStack>
          );
        },
      }),
      columnHelper.accessor('node.createdAt', {
        header: `${t('admin.assigments.tableHeader.createdOn')}`,
        cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
        footer: (info) => info.column.id,
      }),
      columnHelper.display({
        id: 'createdBy',
        header: `${t('admin.assigments.tableHeader.createdBy')}`,
        cell: (info) => (
          <span>{info.row.original.node.user?.fullName || t('global.users.removed')}</span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const { node } = info.row.original;
          const { enabled, settings, id } = node;
          const { type: workflowType } = settings;

          return (
            <WarmNeutralTableActionsMenu
              actions={[
                {
                  label: enabled
                    ? t('admin.assignments.details.actions.deactivate')
                    : t('admin.assignments.details.actions.activate'),
                  onClick: (e) => {
                    e.stopPropagation();

                    return enabled
                      ? deactivateWorkflowMutation.mutateAsync({
                          workflowId: id,
                          refetchAction: refetch,
                        })
                      : activateWorkflowMutation.mutateAsync({
                          workflowId: id,
                          refetchAction: refetch,
                        });
                  },

                  shouldRender: workflowType !== WorkflowType.PointInTime,
                },
                {
                  label: t('admin.assignments.details.actions.editName'),
                  shouldRender: true,
                  onClick: (e) => {
                    e.stopPropagation();
                    setSelectedWorkflowId(id);
                    setEditNameModalOpen(true);
                  },
                },
                {
                  label: t('admin.assignments.details.actions.duplicate'),
                  onClick: (e) => {
                    e.stopPropagation();
                    setSelectedWorkflowId(id);
                    setConfirmationModalType('duplicate');
                    setConfirmationModalOpen(true);
                  },
                  shouldRender: true,
                },
              ]}
            />
          );
        },
      }),
    ],
    [],
  );

  const getDuplicateWorkflowDetailsMutation = useMutation({
    mutationFn: (id: string) => AdminAssignmentsService.getWorkflowDetails(id),
  });

  if (!permissions.view) return <Navigate to="/explore" replace />;

  if (
    selectedItems.length === 0 &&
    !isLoading &&
    data &&
    data.edges.length === 0 &&
    assignmentsCreate
  ) {
    return (
      <CreateAssignmentStartPage onCreateAssignment={() => navigate('/admin/assignments/create')} />
    );
  }

  return (
    <Stack>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingX={12}
        borderBottom="1px solid"
        borderBottomColor="neutral.0"
        backgroundColor="white"
      >
        <Text as="h1" fontSize="28px" fontWeight={600}>
          {pageTitleMap[type as keyof typeof pageTitleMap] || t('admin.assignments')}
        </Text>

        {assignmentsCreate && (
          <SolidButton
            leftIcon={<Plus boxSize={3} />}
            onClick={() => navigate('/admin/assignments/create')}
          >
            {t('admin.assignments.newAssignment')}
          </SolidButton>
        )}
      </Flex>

      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingRight={12}
        paddingLeft={6}
        paddingY={6}
      >
        <ButtonGroup>
          {assignmentsAllowed && workflowsAllowed && (
            <AssignmentTabButton
              label="All"
              status={type === 'all' ? 'active' : 'inactive'}
              onClick={() => {
                setPagination({ pageIndex: 0, pageSize: 50 });
                setSearchParams({ type: 'all' });
              }}
            />
          )}

          {assignmentsAllowed && (
            <AssignmentTabButton
              label="Direct"
              status={type === 'direct' ? 'active' : 'inactive'}
              onClick={() => {
                setPagination({ pageIndex: 0, pageSize: 50 });
                setSearchParams({ type: 'direct' });
              }}
            />
          )}

          {workflowsAllowed && (
            <AssignmentTabButton
              label="Automated"
              status={type === 'automated' ? 'active' : 'inactive'}
              onClick={() => {
                setPagination({ pageIndex: 0, pageSize: 50 });
                setSearchParams({ type: 'automated' });
              }}
            />
          )}
        </ButtonGroup>

        <WorkflowSearch />
      </Flex>

      <AssignmentsTable
        columns={columns}
        data={data?.edges}
        columnVisibility={columnVisibility}
        getRowId={getRowId}
        pagination={pagination}
        setPagination={setPagination}
        totalResultCount={data?.pageInfo.totalCount ?? 0}
        isLoading={isLoading || isFetching}
        onRowClick={(row) => navigate(`/admin/assignments/details/${row.id}`)}
      />

      {confirmationModalOpen && confirmationModalType !== null && (
        <AssignmentConfirmationModal
          isOpen={confirmationModalOpen && confirmationModalType !== null}
          onClose={() => {
            setConfirmationModalOpen(false);
            setConfirmationModalType(null);
          }}
          modalType={confirmationModalType}
          submitLoading={getDuplicateWorkflowDetailsMutation.isLoading}
          onSubmit={async () => {
            if (confirmationModalType === 'duplicate') {
              getDuplicateWorkflowDetailsMutation
                .mutateAsync(selectedWorkflowId)
                .then((duplicateWorkflowDetails) => {
                  setSelectedWorkflowDuplicateData(duplicateWorkflowDetails as Workflow);

                  navigate('/admin/assignments/create', {
                    state: { workflowId: selectedWorkflowId },
                  });
                })
                .catch((error) => {
                  console.error('Error duplicating workflow:', error);

                  setToast({
                    show: true,
                    status: 'error',
                    title: t('admin.assignments.duplicate.error'),
                  });
                });
            }
          }}
        />
      )}

      <AssignmentEditNameModal
        id={selectedWorkflowId}
        isOpen={editNameModalOpen}
        onClose={() => {
          setSelectedWorkflowId('');
          setEditNameModalOpen(false);
        }}
        refetchAction={refetch}
      />
    </Stack>
  );
}
